/**
 * Never-throw client for the companion BE error-log + Lark Q&A.
 * Uses raw fetch so a failed report cannot recurse through api-client
 * capture, cannot toast, and cannot throw into the page.
 *
 * Interview system string is `GET /client-errors/qa-prompt` → `prompt`
 * (`LARK_ERROR_QA_PROMPT`). That is the interview. Cab-log
 * `LARK_VOICE_INSTRUCTIONS` is not fetched here. This module does
 * not paraphrase `LARK_PERSONALITY`.
 */

import { redactSecrets } from '@/lib/lark-error-redact';
import type { LarkErrorEvent } from '@/lib/lark-error-types';
import { extractLarkErrorQaPrompt, type LarkErrorQaPrompt } from '@/lib/lark-voice-from-backend';
import { tokenStorage } from '@/utils/token-storage';

const CLIENT_ERRORS_PATH = '/client-errors';

export type ClientErrorCreateBody = {
  origin: 'system';
  errorId: string;
  path: string;
  toastTitle: string;
  toastBody: string;
  userAgent?: string;
};

export type ClientErrorQaPrompt = LarkErrorQaPrompt;

function apiBase(): string {
  return import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api';
}

function authHeader(): Record<string, string> {
  try {
    const token = tokenStorage.getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

function jsonSafe(value: unknown): unknown {
  try {
    return JSON.parse(redactSecrets(JSON.stringify(value)));
  } catch {
    return {};
  }
}

/**
 * Create the capture session. Requires a server-minted errorId (5xx
 * body.errorId / X-Lark-Error-Id). No id → no POST. POST fail → null.
 */
export async function postClientError(body: ClientErrorCreateBody): Promise<string | null> {
  try {
    const errorId = body.errorId.trim();
    if (!errorId) return null;
    const response = await fetch(`${apiBase()}${CLIENT_ERRORS_PATH}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authHeader(),
      },
      body: JSON.stringify(
        jsonSafe({
          origin: 'system',
          errorId,
          path: body.path,
          toastTitle: body.toastTitle,
          toastBody: body.toastBody,
          userAgent: body.userAgent,
        }),
      ),
    });
    if (!response.ok) return null;
    return errorId;
  } catch {
    return null;
  }
}

export async function postClientErrorEvent(
  systemErrorId: string,
  event: LarkErrorEvent,
): Promise<void> {
  try {
    if (!systemErrorId) return;
    const kind =
      event.kind === 'blocked_prompt' || event.kind === 'off_topic_redirect' || event.kind === 'off_topic_end'
        ? 'blocked_prompt'
        : 'interaction';
    await fetch(
      `${apiBase()}${CLIENT_ERRORS_PATH}/${encodeURIComponent(systemErrorId)}/events`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeader(),
        },
        body: JSON.stringify(jsonSafe({ kind, ...event.payload, blocked: kind === 'blocked_prompt' })),
      },
    );
  } catch {
    /* never throw */
  }
}

/**
 * Send the interview answers. Returns true only when the BE accepted
 * the Q&A event, so the sheet can show an honest acknowledgement.
 * Still never throws.
 */
export async function postClientErrorQa(
  systemErrorId: string,
  transcript: Array<{ question: string; answer: string }>,
): Promise<boolean> {
  try {
    if (!systemErrorId || transcript.length === 0) return false;
    const response = await fetch(
      `${apiBase()}${CLIENT_ERRORS_PATH}/${encodeURIComponent(systemErrorId)}/events`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeader(),
        },
        body: JSON.stringify(jsonSafe({ kind: 'qa', transcript })),
      },
    );
    return response.ok;
  } catch {
    /* never throw */
    return false;
  }
}

/**
 * Interview system string: `LARK_ERROR_QA_PROMPT` from the companion
 * BE (`GET /client-errors/qa-prompt` → `prompt`). Fail-open: null if
 * GET fails. Does not start a cab voice session.
 */
export async function fetchClientErrorQaPrompt(): Promise<ClientErrorQaPrompt | null> {
  try {
    const response = await fetch(`${apiBase()}${CLIENT_ERRORS_PATH}/qa-prompt`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...authHeader(),
      },
    });
    if (!response.ok) return null;
    const body = await response.json().catch(() => null);
    return extractLarkErrorQaPrompt(body);
  } catch {
    return null;
  }
}
